import { useCallback, useEffect, useRef, useState } from 'react';
import {
  calendarEventsService,
  type DueCalendarReminder,
} from '@core/supabase/services/calendarEventsService';

const POLL_MS = 45_000;

function formatReminderBody(r: DueCalendarReminder): string {
  const start = new Date(r.event_starts_at);
  const when = start.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  if (r.offset_minutes === 0) return `${r.event_title} — starting now (${when})`;
  if (r.offset_minutes < 60) return `${r.event_title} — starts in ${r.offset_minutes}m (${when})`;
  if (r.offset_minutes < 1440) return `${r.event_title} — starts in ${Math.round(r.offset_minutes / 60)}h (${when})`;
  return `${r.event_title} — starts ${when}`;
}

/**
 * Polls due calendar reminders; optional browser Notification API (deduped per session).
 */
export function useCalendarReminders(orgId: string | null | undefined, userId: string | null | undefined) {
  const [due, setDue] = useState<DueCalendarReminder[]>([]);
  const [loading, setLoading] = useState(false);
  const notifiedRef = useRef<Set<string>>(new Set());

  const refresh = useCallback(async () => {
    if (!orgId || !userId) {
      setDue([]);
      return;
    }
    setLoading(true);
    try {
      const rows = await calendarEventsService.listDueReminders(orgId, userId);
      setDue(rows);

      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        for (const r of rows) {
          if (notifiedRef.current.has(r.id)) continue;
          notifiedRef.current.add(r.id);
          try {
            new Notification('Calendar reminder', {
              body: formatReminderBody(r),
              tag: r.id,
            });
          } catch {
            /* ignore */
          }
        }
      }
    } catch {
      /* keep prior */
    } finally {
      setLoading(false);
    }
  }, [orgId, userId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!orgId || !userId) return;
    const t = window.setInterval(refresh, POLL_MS);
    return () => window.clearInterval(t);
  }, [orgId, userId, refresh]);

  const acknowledge = useCallback(
    async (reminderId: string) => {
      if (!orgId || !userId) return;
      await calendarEventsService.acknowledgeReminder(orgId, userId, reminderId);
      setDue((prev) => prev.filter((r) => r.id !== reminderId));
    },
    [orgId, userId]
  );

  const requestBrowserPermission = useCallback(async () => {
    if (typeof Notification === 'undefined') return 'unsupported' as const;
    if (Notification.permission === 'granted') return 'granted' as const;
    if (Notification.permission === 'denied') return 'denied' as const;
    const p = await Notification.requestPermission();
    return p === 'granted' ? ('granted' as const) : ('denied' as const);
  }, []);

  return { dueReminders: due, loading, refresh, acknowledge, requestBrowserPermission };
}
